const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const EVENT_ENDPOINT = `${API_BASE_URL}/interview/event`;
const AUDIO_UPLOAD_ENDPOINT = `${API_BASE_URL}/interview/audio-snippet`;
const SILENT_THRESHOLD = 0.01;
const LOUD_THRESHOLD = 0.3;
const SNIPPET_INTERVAL_MS = 5000;

class AudioAnalyzer {
  private sessionId: string;
  private _sendEvent: (payload: any) => Promise<void>;
  private _uploadSnippet: (payload: any) => Promise<void>;
  private _audioContext: AudioContext | null = null;
  private _analyser: AnalyserNode | null = null;
  private _source: MediaStreamAudioSourceNode | null = null;
  private _data: Float32Array | null = null;
  private _running: boolean = false;
  private _lastSnippetTime: number = 0;
  private _frameHandle: number | null = null;
  private _mediaRecorder: MediaRecorder | null = null;
  private _chunks: Blob[] = [];
  private _snippetTimer: NodeJS.Timeout | null = null;
  private _lastEvents: { [key: string]: number } = {};
  private _lastAudioState: 'silent' | 'normal' | 'loud' | null = null;
  private _silentFrameCount: number = 0;
  private _loudFrameCount: number = 0;

  constructor(sessionId: string, options: any = {}) {
    this.sessionId = sessionId;
    this._sendEvent = options.sendEvent || this._defaultSendEvent;
    this._uploadSnippet = options.uploadSnippet || this._defaultUploadSnippet;
  }

  async start(stream: MediaStream) {
    if (this._running) {
      return;
    }
    if (!stream) {
      throw new Error("Stream is required to start audio analysis");
    }


    this._audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    this._analyser = this._audioContext.createAnalyser();
    this._analyser.fftSize = 2048;
    this._source = this._audioContext.createMediaStreamSource(stream);
    this._source.connect(this._analyser);
    this._data = new Float32Array(this._analyser.fftSize);
    this._running = true;

    // Try to start MediaRecorder for audio snippets (optional - not required for analysis)

    if (window.MediaRecorder && stream.getAudioTracks().length > 0) {
      try {
        // Check for supported mimeTypes
        const supportedTypes = [
          "audio/webm",
          "audio/webm;codecs=opus",
          "audio/ogg;codecs=opus",
          "audio/mp4",
        ];
        
        let selectedMimeType: string | undefined;
        for (const mimeType of supportedTypes) {
          if (MediaRecorder.isTypeSupported(mimeType)) {
            selectedMimeType = mimeType;
            break;
          }
        }

        if (selectedMimeType) {
          this._mediaRecorder = new MediaRecorder(stream, {
            mimeType: selectedMimeType,
          });
          this._mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              this._chunks.push(event.data);
            }
          };
          
          // Check if recorder is already in use or in wrong state
          if (this._mediaRecorder.state === "inactive") {
            this._mediaRecorder.start(1000);
            this._snippetTimer = setInterval(() => {
              this._exportSnippet();
            }, SNIPPET_INTERVAL_MS);
          } else {
            this._mediaRecorder = null;
          }
        } else {
        }
      } catch (error: any) {
        // Continue without MediaRecorder - audio analysis will still work
        this._mediaRecorder = null;
      }
    }

    this._analyze();
  }

  stop() {
    this._running = false;
    if (this._frameHandle) {
      cancelAnimationFrame(this._frameHandle);
      this._frameHandle = null;
    }
    if (this._snippetTimer) {
      clearInterval(this._snippetTimer);
      this._snippetTimer = null;
    }
    if (this._mediaRecorder) {
      this._mediaRecorder.stop();
      this._mediaRecorder = null;
    }
    this._chunks = [];

    if (this._source) {
      this._source.disconnect();
      this._source = null;
    }
    if (this._analyser) {
      this._analyser.disconnect();
      this._analyser = null;
    }
    if (this._audioContext) {
      this._audioContext.close();
      this._audioContext = null;
    }
  }

  private _analyze() {
    if (!this._running || !this._analyser || !this._data) {
      return;
    }
    
    this._analyser.getFloatTimeDomainData(this._data as any);
    const rms =
      Math.sqrt(
        this._data.reduce((sum, value) => sum + value * value, 0) /
          this._data.length
      ) || 0;

    // Determine current audio state
    let currentState: 'silent' | 'normal' | 'loud';
    if (rms < SILENT_THRESHOLD) {
      currentState = 'silent';
      this._silentFrameCount++;
      this._loudFrameCount = 0;
    } else if (rms > LOUD_THRESHOLD) {
      currentState = 'loud';
      this._loudFrameCount++;
      this._silentFrameCount = 0;
    } else {
      currentState = 'normal';
      this._silentFrameCount = 0;
      this._loudFrameCount = 0;
    }

    // Only send events on state changes after sustained periods
    // Require at least 60 frames (~1 second at 60fps) of sustained silence before reporting
    // This prevents false positives from brief pauses in speech
    if (currentState === 'silent') {
      if (this._silentFrameCount >= 60 && this._lastAudioState !== 'silent') {
        // Only log and send event when transitioning TO silent state (after sustained silence)
        // This is just for proctoring tracking, not for showing alerts to user
        this._throttledEvent("audio_silent", { rms, sustainedFrames: this._silentFrameCount });
        this._lastAudioState = 'silent';
      }
    } else if (currentState === 'loud') {
      if (this._lastAudioState !== 'loud') {
        // Report loud immediately on state change (less frequent naturally)
        this._throttledEvent("audio_loud", { rms });
        this._lastAudioState = 'loud';
      }
    } else if (currentState === 'normal') {
      if (this._lastAudioState !== 'normal' && this._lastAudioState !== null) {
        // Only log when transitioning FROM silent/loud TO normal
      }
      this._lastAudioState = 'normal';
      this._silentFrameCount = 0;
      this._loudFrameCount = 0;
    }

    this._frameHandle = requestAnimationFrame(() => this._analyze());
  }

  private async _throttledEvent(type: string, details: any = {}) {
    if (!this.sessionId) {
      return;
    }
    const now = performance.now();
    const last = this._lastEvents[type] || 0;
    // Increase throttling: 10 seconds for silent, 5 seconds for loud
    const throttleMs = type === 'audio_silent' ? 10000 : 5000;
    if (now - last < throttleMs) {
      return;
    }
    this._lastEvents[type] = now;
    await this._sendEvent({
      sessionId: this.sessionId,
      type,
      details,
      timestamp: new Date().toISOString(),
    });
  }

  private async _exportSnippet() {
    if (!this.sessionId || this._chunks.length === 0) {
      return;
    }
    const blob = new Blob(this._chunks, { type: "audio/webm" });
    this._chunks = [];
    const arrayBuffer = await blob.arrayBuffer();
    const base64 = this._arrayBufferToBase64(arrayBuffer);
    await this._uploadSnippet({
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      audioBase64: base64,
      mimeType: blob.type,
    });
  }

  private _arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  private async _defaultSendEvent(payload: any) {
    try {
      await fetch(EVENT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      // Event sent
    } catch (error) {
      // Event send failed
    }
  }

  private async _defaultUploadSnippet(payload: any) {
    try {
      await fetch(AUDIO_UPLOAD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      // Snippet uploaded
    } catch (error) {
      // Snippet upload failed
    }
  }
}

export function createAudioAnalyzer(sessionId: string, options?: any) {
  return new AudioAnalyzer(sessionId, options);
}

export default AudioAnalyzer;

